import React, { useState, useEffect } from "react";
import MasterTable from "../common/MasterTable";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import Loader from "../Loader/Loader";
import { format, isValid, parse } from "date-fns";
import HeaderWithBreadcrumb from "../common/HeaderWithBreadcrumb";
import AcceptOtpModal from "./AcceptOtpModal";
import BagCourierFilterModal from "./BagCourierFilterModal";
import FilterBar from "../common/FilterBar";

// The hub's bag list only ever shows bags sent to it or accepted by it
const HUB_BAG_STATUS_OPTIONS = [
  { value: "NOTIFIED_HUB", label: "Notified Hub" },
  { value: "ACCEPTED_BY_HUB", label: "Accepted by Hub" },
];

const defaultFilterData = {
  bagNo: "",
  fromDate: "",
  toDate: "",
  createdBy: "",
  status: "",
  search: "",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      // yyyy-MM-dd -> dd-MM-yyyy
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  }
  return dateStr;
};

const getItemDateString = (item) => {
  const raw = item?.rawCreatedDate || item?.createdDate;
  if (!raw || typeof raw !== "string") return "";
  const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  const matchIso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (matchIso) {
    return `${matchIso[1]}-${matchIso[2]}-${matchIso[3]}`;
  }
  return "";
};

const BagCourier = () => {
  const [pageSize, setPageSize] = useState(10);
  const [noData, setNoData] = useState(false);
  const [editData, setEditData] = useState({});
  const [customerData, setCustomerData] = useState([]);
  const [modalAction, setModalAction] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageDetails, setPageDetails] = useState(null);
  const loggedInUserName = sessionStorage.getItem("username");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [columnData, setColumnData] = useState("");
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);

  const openOtpModal = () => setIsOtpModalOpen(true);
  const closeOtpModal = () => setIsOtpModalOpen(false);

  const openModalFilter = () => setIsModalOpenFilter(true);
  const closeModalFilter = () => setIsModalOpenFilter(false);

  useEffect(() => {
    fetchBagData(1);
  }, []);

  // fetch bag data
  const fetchBagData = async (
    currentPage = 1,
    filters = filterData,
    size = pageSize
  ) => {
    setLoading(true);

    try {
      const requestBody = {
        username: loggedInUserName,
        statusList: filters?.status
          ? [filters.status]
          : HUB_BAG_STATUS_OPTIONS.map((o) => o.value),
        search: filters?.search || "",
        bagNo: filters?.bagNo || "",
        fromDate: formatDate(filters?.fromDate),
        toDate: formatDate(filters?.toDate),
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_BAG_MASTER}?&pageSize=${size}&pageNo=${currentPage}`,
        requestBody
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setLoading(false);
        setPageDetails(response?.data?.pageDetails);
        let modifiedData = modifyStructure(response?.data?.data);

        // Fallback / client-side filter
        if (filters?.bagNo && filters.bagNo.trim() !== "") {
          const targetBag = filters.bagNo.trim().toLowerCase();
          modifiedData = modifiedData.filter(
            (item) => (item.bagNo || "").trim().toLowerCase() === targetBag
          );
        }
        if (filters?.createdBy && filters.createdBy.trim() !== "") {
          const targetUser = filters.createdBy.trim().toLowerCase();
          modifiedData = modifiedData.filter(
            (item) => (item.createdBy || "").trim().toLowerCase() === targetUser
          );
        }
        if (filters?.fromDate && filters.fromDate.trim() !== "") {
          const from = filters.fromDate.trim();
          modifiedData = modifiedData.filter((item) => {
            const d = getItemDateString(item);
            return d ? d >= from : true;
          });
        }
        if (filters?.toDate && filters.toDate.trim() !== "") {
          const to = filters.toDate.trim();
          modifiedData = modifiedData.filter((item) => {
            const d = getItemDateString(item);
            return d ? d <= to : true;
          });
        }

        setCustomerData(modifiedData);
        if (modifiedData?.length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
        }
      } else {
        setLoading(false);
        setNoData(true);
        setCustomerData([]);
      }
    } catch (error) {
      setLoading(false);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((user, index) => {
      let formattedCreDate = null;
      let formattedStrDate = null;

      if (user?.createdDate) {
        const parsed = parse(
          user.createdDate,
          "dd-MM-yyyy HH:mm:ss",
          new Date()
        );
        if (isValid(parsed)) {
          formattedCreDate = parsed;
        } else {
          console.warn("Invalid createdDate:", user.createdDate);
        }
      }

      if (user?.startDateTime) {
        const parsed = parse(
          user.startDateTime,
          "dd-MM-yyyy HH:mm:ss",
          new Date()
        );
        if (isValid(parsed)) {
          formattedStrDate = parsed;
        } else {
          console.warn("Invalid startDateTime:", user.startDateTime);
        }
      }

      const itemCount = user.itemsIncluded?.length || 0;

      const itemsIncludedIds = Array.isArray(user.itemsIncluded)
        ? user.itemsIncluded
        : []; // backend expects array

      return {
        ...user,
        rawCreatedDate: user.createdDate,
        itemsIncludedIds,

        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : "",

        startDateTime: formattedStrDate
          ? [
              format(formattedStrDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedStrDate, "HH:mm:ss"),
            ]
          : "",

        itemsIncluded: (
          // <span className="text-blue-600 underline cursor-pointer">
          <span>
            {itemCount > 0
              ? `${itemCount} Item${itemCount > 1 ? "s" : ""}`
              : "No Items"}
          </span>
        ),
      };
    });
  };

  const customerHeader = [
    "Bag No",
    "Created Date",
    "Created By",
    "Status",
    "Items Included",
    "Start date & Time",
  ];

  const customerHeaderToKeyMap = {
    "Bag No": "bagNo",
    "Created Date": "createdDate",
    "Created By": "createdBy",
    Status: "status",
    "Items Included": "itemsIncluded",
    "Start date & Time": "startDateTime",
  };

  const handleAuthorization = (data, actionType = "view") => {
    console.log("::data::", data);
  };

  const handleCustomer = (data, actionType = "view") => {
    setModalAction(actionType);
    setColumnData(data);

    if (actionType === "acceptOtp") {
      // Open OTP modal instead of changing status directly
      setEditData(data);
      // openOtpModal();
      handleBagAccept(data);
    } else {
      let custData = customerData.find((item) => item?.id === data?.id);
      if (custData) {
        setEditData(custData);
      }
    }
  };

  const handleBagAccept = async (data) => {
    try {
      const payload = {
        username: loggedInUserName,
        bagNo: data?.bagNo,
      };

      const response = await axiosPostApi(apiList.ACCEPT_BAG_BY_HUB, payload);
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        CustomAlert({
          title: response?.data?.message,
          icon: "success",
          confirmButtonText: "OK",
          showCloseButton: true,
          allowOutsideClick: false,
        });
        fetchBagData(currentPage);
      } else if (response?.status === 200 && !response?.data?.status) {
        CustomAlert({
          title: "Failed to Request",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const applyFilters = (filters = filterData) => {
    setFilterData(filters);
    setCurrentPage(1);
    fetchBagData(1, filters);
  };

  const handleReset = () => {
    setFilterData(defaultFilterData);
    setCurrentPage(1);
    fetchBagData(1, defaultFilterData);
  };

  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <>
      <HeaderWithBreadcrumb title="Bag Master" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={applyFilters}
        search={{ key: "search", placeholder: "Search bag no., created by or notification ID" }}
        selects={[{ key: "status", allLabel: "All statuses", chipLabel: "Status", options: HUB_BAG_STATUS_OPTIONS }]}
        dateRange={{ fromKey: "fromDate", toKey: "toDate", label: "Created" }}
      />
      <div>
        <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
          {loading ? (
            <Loader />
          ) : (
            <>
              <MasterTable
                loading={loading}
                headers={customerHeader}
                data={customerData}
                handleAction={handleCustomer}
                filterFields={["bagNo", "createdBy"]}
                entityType="BagCourier"
                headerToKeyMap={customerHeaderToKeyMap}
                handleAuth={handleAuthorization}
                noData={noData}
                pageDetails={pageDetails}
                currentPage={currentPage}
                pageSize={pageSize}
                totalRecord={totalRecord}
                fetchNextData={(page, filters = filterData, size) =>
                  fetchBagData(page, filters, size)
                }
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                handleReset={handleReset}
                fetchData={handleReset}
                filterData={filterData}
                openModalFilter={openModalFilter}
                hideFilterControls
              />
            </>
          )}

          {isModalOpenFilter && (
            <BagCourierFilterModal
              show={isModalOpenFilter}
              handleClose={closeModalFilter}
              filterData={filterData}
              setFilterData={setFilterData}
              handleReset={handleReset}
              bagList={customerData}
              applyFilters={applyFilters}
            />
          )}

          {isOtpModalOpen && (
            <AcceptOtpModal
              show={isOtpModalOpen}
              handleClose={closeOtpModal}
              bagData={editData}
              onSubmit={({ sender, receiver, bagData }) => {
                console.log(
                  "Sender OTP:",
                  sender,
                  "Receiver OTP:",
                  receiver,
                  bagData
                );

                // Example: Mark bag as accepted
                setCustomerData((prev) =>
                  prev.map((bag) =>
                    bag.companyId === bagData.companyId
                      ? { ...bag, status: "Accepted By Courier Hub" }
                      : bag
                  )
                );
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BagCourier;
